import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { 
  Play, RotateCcw, ChevronLeft, ChevronRight, Check, X, 
  Clock, Cpu, AlertCircle, Loader2, Code, FileText, 
  CheckCircle, XCircle, Terminal
} from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { toast, Toaster } from 'sonner';
import Editor from '@monaco-editor/react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LANGUAGE_TEMPLATES = {
  python: `# Write your solution here
def solution():
    # Your code here
    pass

# Read input
if __name__ == "__main__":
    # Read and process input
    pass
`,
  javascript: `// Write your solution here
function solution() {
    // Your code here
}

// Read input from stdin
const readline = require('readline');
const rl = readline.createInterface({
    input: process.stdin,
    output: process.stdout
});
`,
  cpp: `#include <iostream>
#include <vector>
#include <string>
using namespace std;

int main() {
    // Your code here
    
    return 0;
}
`,
  java: `import java.util.*;

public class Main {
    public static void main(String[] args) {
        Scanner sc = new Scanner(System.in);
        // Your code here
        
    }
}
`,
  c: `#include <stdio.h>
#include <stdlib.h>

int main() {
    // Your code here
    
    return 0;
}
`
};

const ProblemSolvePage = () => {
  const { problemId } = useParams();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  
  const [problem, setProblem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [language, setLanguage] = useState('python');
  const [code, setCode] = useState(LANGUAGE_TEMPLATES.python);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [activeTab, setActiveTab] = useState('description');
  const [showSidebar, setShowSidebar] = useState(true);

  useEffect(() => {
    fetchProblem();
  }, [problemId]);

  useEffect(() => {
    // Set starter code when language changes
    if (problem?.starter_code?.[language]) {
      setCode(problem.starter_code[language]);
    } else {
      setCode(LANGUAGE_TEMPLATES[language] || '');
    }
  }, [language, problem]);

  const fetchProblem = async () => {
    try {
      const headers = {};
      const token = localStorage.getItem('token');
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      
      const res = await axios.get(`${API}/coding/problems/${problemId}`, { headers });
      setProblem(res.data);
      
      // Set starter code if available
      if (res.data.starter_code?.[language]) {
        setCode(res.data.starter_code[language]);
      }
    } catch (err) {
      console.error('Failed to fetch problem:', err);
      toast.error('Problem not found');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!user) {
      toast.error('Please login to submit');
      navigate('/login');
      return;
    }

    setSubmitting(true);
    setResult(null);

    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(
        `${API}/coding/submit`,
        {
          problem_id: problemId,
          language: language,
          source_code: code
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const submissionId = res.data.submission_id;
      toast.success('Code submitted! Processing...');

      // Poll for results
      let attempts = 0;
      const maxAttempts = 30;
      
      const pollInterval = setInterval(async () => {
        attempts++;
        try {
          const statusRes = await axios.get(
            `${API}/coding/submission/${submissionId}`,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          
          if (statusRes.data.status === 'Completed' || statusRes.data.status === 'Error') {
            clearInterval(pollInterval);
            setResult(statusRes.data);
            setSubmitting(false);
            
            if (statusRes.data.verdict === 'Accepted') {
              toast.success('All test cases passed! 🎉');
            } else {
              toast.error(`Verdict: ${statusRes.data.verdict}`);
            }
          }
          
          if (attempts >= maxAttempts) {
            clearInterval(pollInterval);
            setSubmitting(false);
            toast.error('Submission timed out');
          }
        } catch (err) {
          console.error('Poll error:', err);
        }
      }, 1000);

    } catch (err) {
      console.error('Submit error:', err);
      toast.error(err.response?.data?.detail || 'Submission failed');
      setSubmitting(false);
    }
  };

  const handleReset = () => {
    if (problem?.starter_code?.[language]) {
      setCode(problem.starter_code[language]);
    } else {
      setCode(LANGUAGE_TEMPLATES[language] || '');
    }
    setResult(null);
  };

  const getDifficultyStyle = (diff) => {
    switch (diff) {
      case 'easy': return 'text-emerald-600 bg-emerald-50 border-emerald-200';
      case 'medium': return 'text-amber-600 bg-amber-50 border-amber-200';
      case 'hard': return 'text-rose-600 bg-rose-50 border-rose-200';
      default: return 'text-slate-600 bg-slate-50 border-slate-200';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <Loader2 className="animate-spin text-indigo-500" size={48} />
      </div>
    );
  }

  if (!problem) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="mx-auto text-slate-400 mb-4" size={48} />
          <h2 className="text-xl font-semibold text-white mb-2">Problem Not Found</h2>
          <Link to="/coding/problems" className="text-indigo-400 hover:text-indigo-300">
            Back to Problems
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen bg-slate-900 flex flex-col overflow-hidden">
      <Toaster position="top-right" richColors />
      
      {/* Top Bar */}
      <div className="h-14 bg-slate-800 border-b border-slate-700 flex items-center justify-between px-4 shrink-0">
        <div className="flex items-center gap-4">
          <Link 
            to="/coding/problems" 
            className="flex items-center gap-2 text-slate-400 hover:text-white transition-colors"
          >
            <ChevronLeft size={20} />
            <span className="hidden sm:inline">Problems</span>
          </Link>
          
          <div className="h-6 w-px bg-slate-700"></div>
          
          <div className="flex items-center gap-3">
            <span className="font-semibold text-white truncate max-w-[200px] sm:max-w-none">
              {problem.title}
            </span>
            <span className={`px-2 py-0.5 rounded text-xs font-medium border ${getDifficultyStyle(problem.difficulty)}`}>
              {problem.difficulty?.toUpperCase()}
            </span>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <select
            value={language}
            onChange={(e) => setLanguage(e.target.value)}
            className="bg-slate-700 text-white border border-slate-600 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
            data-testid="language-select"
          >
            <option value="python">Python</option>
            <option value="javascript">JavaScript</option>
            <option value="cpp">C++</option>
            <option value="java">Java</option>
            <option value="c">C</option>
          </select>

          <button
            onClick={handleReset}
            className="p-2 text-slate-400 hover:text-white hover:bg-slate-700 rounded-lg transition-colors"
            title="Reset Code"
          >
            <RotateCcw size={18} />
          </button>

          <button
            onClick={handleSubmit}
            disabled={submitting}
            className="flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 disabled:bg-emerald-800 text-white px-4 py-2 rounded-lg font-medium transition-colors"
            data-testid="submit-code-btn"
          >
            {submitting ? (
              <>
                <Loader2 className="animate-spin" size={18} />
                Running...
              </>
            ) : (
              <>
                <Play size={18} />
                Submit
              </>
            )}
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex overflow-hidden">
        {/* Left Panel - Problem Description */}
        <div className={`${showSidebar ? 'w-[45%]' : 'w-0'} bg-slate-800 border-r border-slate-700 flex flex-col transition-all duration-300 overflow-hidden`}>
          {/* Tabs */}
          <div className="flex border-b border-slate-700 shrink-0">
            <button
              onClick={() => setActiveTab('description')}
              className={`px-4 py-3 text-sm font-medium transition-colors ${
                activeTab === 'description' 
                  ? 'text-indigo-400 border-b-2 border-indigo-400' 
                  : 'text-slate-400 hover:text-white'
              }`}
            >
              <FileText size={16} className="inline mr-2" />
              Description
            </button>
            <button
              onClick={() => setActiveTab('submissions')}
              className={`px-4 py-3 text-sm font-medium transition-colors ${
                activeTab === 'submissions' 
                  ? 'text-indigo-400 border-b-2 border-indigo-400' 
                  : 'text-slate-400 hover:text-white'
              }`}
            >
              <Terminal size={16} className="inline mr-2" />
              Results
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-6">
            {activeTab === 'description' ? (
              <div className="prose prose-invert max-w-none">
                <div className="mb-6">
                  <h1 className="text-2xl font-bold text-white mb-4">{problem.title}</h1>
                  <div className="flex flex-wrap gap-2 mb-4">
                    {problem.tags?.map((tag, idx) => (
                      <span key={idx} className="px-2 py-1 bg-slate-700 text-slate-300 rounded text-xs">
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>

                <div className="text-slate-300 whitespace-pre-wrap mb-6">
                  {problem.description}
                </div>

                {problem.input_format && (
                  <div className="mb-6">
                    <h3 className="text-lg font-semibold text-white mb-2">Input Format</h3>
                    <p className="text-slate-300">{problem.input_format}</p>
                  </div>
                )}

                {problem.output_format && (
                  <div className="mb-6">
                    <h3 className="text-lg font-semibold text-white mb-2">Output Format</h3>
                    <p className="text-slate-300">{problem.output_format}</p>
                  </div>
                )}

                {problem.constraints && (
                  <div className="mb-6">
                    <h3 className="text-lg font-semibold text-white mb-2">Constraints</h3>
                    <p className="text-slate-300 font-mono text-sm">{problem.constraints}</p>
                  </div>
                )}

                {problem.sample_test_cases?.length > 0 && (
                  <div className="mb-6">
                    <h3 className="text-lg font-semibold text-white mb-4">Examples</h3>
                    {problem.sample_test_cases.map((tc, idx) => (
                      <div key={idx} className="mb-4 bg-slate-900 rounded-lg p-4">
                        <div className="mb-3">
                          <div className="text-xs text-slate-500 mb-1">Input:</div>
                          <pre className="bg-slate-950 p-3 rounded text-sm text-emerald-400 overflow-x-auto">
                            {tc.input}
                          </pre>
                        </div>
                        <div>
                          <div className="text-xs text-slate-500 mb-1">Output:</div>
                          <pre className="bg-slate-950 p-3 rounded text-sm text-emerald-400 overflow-x-auto">
                            {tc.expected_output}
                          </pre>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <div>
                <h3 className="text-lg font-semibold text-white mb-4">Submission Results</h3>
                {result ? (
                  <div className="space-y-4">
                    {/* Verdict */}
                    <div className={`p-4 rounded-lg ${
                      result.verdict === 'Accepted' 
                        ? 'bg-emerald-500/20 border border-emerald-500/30' 
                        : 'bg-rose-500/20 border border-rose-500/30'
                    }`}>
                      <div className="flex items-center gap-3">
                        {result.verdict === 'Accepted' ? (
                          <CheckCircle className="text-emerald-400" size={24} />
                        ) : (
                          <XCircle className="text-rose-400" size={24} />
                        )}
                        <div>
                          <div className={`font-bold ${result.verdict === 'Accepted' ? 'text-emerald-400' : 'text-rose-400'}`}>
                            {result.verdict}
                          </div>
                          <div className="text-sm text-slate-400">
                            {result.test_results?.filter(t => t.passed).length || 0} / {result.test_results?.length || 0} test cases passed
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Stats */}
                    <div className="grid grid-cols-2 gap-4">
                      <div className="bg-slate-700/50 rounded-lg p-4">
                        <div className="flex items-center gap-2 text-slate-400 mb-1">
                          <Clock size={16} />
                          <span className="text-sm">Runtime</span>
                        </div>
                        <div className="text-white font-semibold">
                          {result.execution_time ? `${result.execution_time}s` : 'N/A'}
                        </div>
                      </div>
                      <div className="bg-slate-700/50 rounded-lg p-4">
                        <div className="flex items-center gap-2 text-slate-400 mb-1">
                          <Cpu size={16} />
                          <span className="text-sm">Memory</span>
                        </div>
                        <div className="text-white font-semibold">
                          {result.memory_used ? `${(result.memory_used / 1024).toFixed(2)} MB` : 'N/A'}
                        </div>
                      </div>
                    </div>

                    {/* Test Cases */}
                    <div>
                      <h4 className="text-sm font-medium text-slate-400 mb-3">Test Cases</h4>
                      <div className="space-y-2">
                        {result.test_results?.map((test, idx) => (
                          <div 
                            key={idx}
                            className={`flex items-center justify-between p-3 rounded-lg ${
                              test.passed ? 'bg-emerald-500/10' : 'bg-rose-500/10'
                            }`}
                          >
                            <div className="flex items-center gap-2">
                              {test.passed ? (
                                <Check className="text-emerald-400" size={16} />
                              ) : (
                                <X className="text-rose-400" size={16} />
                              )}
                              <span className="text-slate-300">
                                Test Case {test.test_case}
                                {test.is_sample && <span className="text-slate-500 text-xs ml-2">(Sample)</span>}
                              </span>
                            </div>
                            <span className="text-xs text-slate-500">
                              {test.execution_time}s
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <Terminal className="mx-auto text-slate-600 mb-4" size={48} />
                    <p className="text-slate-500">Submit your code to see results</p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Resize Handle */}
        <button
          onClick={() => setShowSidebar(!showSidebar)}
          className="w-1 bg-slate-700 hover:bg-indigo-500 transition-colors cursor-col-resize flex items-center justify-center"
        >
          <div className="absolute">
            {showSidebar ? <ChevronLeft size={16} className="text-slate-400" /> : <ChevronRight size={16} className="text-slate-400" />}
          </div>
        </button>

        {/* Right Panel - Code Editor */}
        <div className="flex-1 flex flex-col bg-slate-900">
          <Editor
            height="100%"
            language={language === 'cpp' ? 'cpp' : language === 'c' ? 'c' : language}
            value={code}
            onChange={(value) => setCode(value || '')}
            theme="vs-dark"
            options={{
              fontSize: 14,
              fontFamily: "'Fira Code', 'Cascadia Code', monospace",
              minimap: { enabled: false },
              scrollBeyondLastLine: false,
              lineNumbers: 'on',
              renderLineHighlight: 'all',
              automaticLayout: true,
              tabSize: 4,
              wordWrap: 'on',
              padding: { top: 16, bottom: 16 }
            }}
          />
        </div>
      </div>
    </div>
  );
};

export default ProblemSolvePage;
